<template>
  <div>
    <div v-for="(moduleGroup, idx) in moduleGroups" :key="idx">
      <!-- Normal Modules -->
      <div v-if="Array.isArray(moduleGroup)">
        <div v-for="module in moduleGroup" :key="module.id" class="module-slot">
          <component
            v-if="module?.module"
            :is="module.module"
            :item="item"
            :module-data="module"
            :type="module.localType"
          />
        </div>
      </div>

      <!-- Tabs -->
      <div v-else-if="Object.values(moduleGroup).length">
        <v-card variant="flat">
          <v-tabs
            v-model="currentTab"
            class="bw-tabs"
            fixed
            hide-slider
          >
            <v-tab
              v-for="(tabModule, index) in Object.values(moduleGroup)"
              :key="tabModule.id"
              :value="index"
              class="bw-tab text-capitalize px-0"
            >
              <span class="bw-tab__text">
                {{ tabModule.settings.label }}
              </span>
            </v-tab>
          </v-tabs>

          <v-window v-model="currentTab" touch>
            <v-window-item
              v-for="(tabModule, index) in Object.values(moduleGroup)"
              :key="tabModule.id"
              :value="index"
            >
              <v-card
                v-for="module in tabModule.modules"
                :key="module.id"
                variant="flat"
              >
                <component
                  v-if="module?.module"
                  :is="module.module"
                  :item="module.item"
                  :module-data="module"
                  :type="module.localType"
                />
              </v-card>
            </v-window-item>
          </v-window>
        </v-card>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineAsyncComponent, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { VCard, VTabs, VTab, VWindow, VWindowItem } from 'vuetify/components'

const props = defineProps({
  moduleData: {
    type: Array,
    default: () => []
  },
  item: {
    type: Object,
    default: () => ({})
  }
})

const route = useRoute()
const currentTab = ref(Number.parseInt(route.params?.tab, 10) || 0)

watch(
  () => route.params?.tab,
  value => {
    currentTab.value = Number.parseInt(value, 10) || 0
  }
)

const modules = {
  Audio: 'Audio',
  DashboardFields: 'Fields',
  DashboardImage: 'Image',
  DashboardItemCards: 'ItemCards',
  DashboardItems: 'PurchasedItems',
  DashboardItemCategoryList: 'ItemCategoryList',
  DashboardPerks: 'DashboardPerks',
  DashboardProductTickets: 'Tickets',
  DashboardVenueList: 'VenueList',
  DealInfo: 'DealInfo',
  Greeting: 'Greeting',
  Html: 'Html',
  HtmlEditor: 'Wisywig',
  ImageShowcase: 'ImageShowcase',
  ItemCards: 'ItemCards',
  ItemVenueListGrid: 'ItemVenueListGrid',
  ItemVenueList: 'ItemVenueListGrid',
  MoreInfo: 'MoreInfo',
  OpeningHours: 'OpeningHours',
  OfferCards: 'ItemCards',
  Products: 'Products',
  ProductInfo: 'ProductInfo',
  PurchasedItems: 'PurchasedItems',
  Tickets: 'Tickets',
  VenueInfo: 'VenueInfo',
  VenueList: 'VenueList',
  Video: 'Video'
}

const moduleImports = import.meta.glob('../*/index.vue')

const moduleGroups = computed(() => {
  let tab
  let afterTabs

  if (!props.moduleData.length) return []

  const source = props.moduleData.map(m => ({ ...m }))
  return source.reduce((acc, module) => {
    if (!module.type) {
      return acc
    }

    if (!['Tab', 'TabsEnd'].includes(module.type) && modules[module.type] == null) {
      return acc
    }

    module.localType = modules[module.type]

    const componentPath = `../${module.localType}/index.vue`
    const importer = moduleImports[componentPath]
    if (!importer) return acc

    if (module.type === 'Tab') tab = module.settings.notes

    if (module.type === 'TabsEnd') {
      tab = false
      afterTabs = true
      return acc
    }

    if (acc.length === 0) acc[0] = !tab ? [] : {}

    if (!tab && afterTabs) {
      afterTabs = false
      acc.push([])
    }

    if (!tab && !afterTabs) {
      module.module = defineAsyncComponent(importer)
      acc[acc.length - 1].push(module)
      return acc
    }

    if (module.type === 'Tab') {
      const idx = Array.isArray(acc[acc.length - 1]) ? acc.length : acc.length - 1

      acc[idx] = {
        ...acc[idx],
        [tab]: { ...module, modules: [] }
      }
      return acc
    }

    module.module = defineAsyncComponent(importer)
    acc[acc.length - 1][tab].modules.push(module)

    return acc
  }, [])
})

watch(moduleGroups, groups => {
  const tabGroup = groups.find(group => !Array.isArray(group))
  if (!tabGroup) return

  const maxIndex = Object.values(tabGroup).length - 1
  if (currentTab.value > maxIndex) currentTab.value = 0
})
</script>

<style scoped lang="scss">
.bw-tabs {
  border-bottom: 1px solid rgb(var(--v-theme-grey-lighten-2));
}

.module-slot {
  display: contents;
}

.bw-tab {
  background-color: rgb(var(--v-theme-grey-lighten-4));
  color: rgb(var(--v-theme-grey));

  &__text {
    font-size: 0.8125rem;
    font-weight: 600;
  }

  &.v-tab--selected {
    background-color: white !important;
    color: black !important;
  }

  &:not(:last-child) {
    border-right: 1px solid rgb(var(--v-theme-grey-lighten-2));
  }
}
</style>
